import type { ProjectDataLLPEvent } from '@avisoma//shared';
import { FaFile } from 'react-icons/fa6';
import { PeekLink } from '../../components/PeekLink';
import { ID } from '../../components/ID';
import { PartEventsTimeline } from './PartEventsTimeline';

interface SelectedPartEventsProps {
  selectedPart: ProjectDataLLPEvent['parts'][number] | null;
}

export function SelectedPartEvents({ selectedPart }: SelectedPartEventsProps) {
  if (!selectedPart) {
    return (
      <div className="flex items-center justify-center min-h-[200px] border border-dashed border-gray-200 dark:border-zinc-700 rounded-lg px-6 py-8 text-sm text-gray-500 dark:text-zinc-400">
        Select a part to view its events
      </div>
    );
  }

  return (
    <div className="sticky top-5 space-y-4 rounded-lg border border-gray-200 p-4 dark:border-zinc-700">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h3 className="text-base font-medium text-gray-900 dark:text-zinc-100">
            {selectedPart.partNumber}
          </h3>
          <p className="text-sm text-gray-500 dark:text-zinc-400">{selectedPart.serialNumber}</p>
        </div>
        <span className="text-xs text-gray-400 dark:text-zinc-500">
          {selectedPart.events.length} events
        </span>
      </div>

      {/* Stale pre-DR-1954 projects.data can miss this field at runtime despite the type (see #727) */}
      {(selectedPart.partMovementPageIds?.length ?? 0) > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-xs text-gray-400 dark:text-zinc-500">Pages</span>
          {selectedPart.partMovementPageIds.map((pageId) => (
            <PeekLink
              type="page"
              id={pageId}
              className="inline-flex items-center gap-1 text-sm"
              key={pageId}
            >
              <FaFile className="size-3" />
              <ID id={pageId} />
            </PeekLink>
          ))}
        </div>
      )}

      {selectedPart.events.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No events found</p>
      ) : (
        <PartEventsTimeline events={selectedPart.events} />
      )}
    </div>
  );
}
